import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, X, Trash2, Pencil, GripVertical, Users, Briefcase, Image as ImageIcon, ImagePlus, CalendarRange, MapPin } from "lucide-react";

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

export default function VehicleCategories() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/vehicle-categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (c) => {
    try {
      await api.patch(`/admin/vehicle-categories/${c.key}`, { active: !c.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Apagar categoria "${c.label}"?`)) return;
    try {
      await api.delete(`/admin/vehicle-categories/${c.key}`);
      toast.success("Categoria apagada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-10" data-testid="vehicle-categories-page">
      <PageHeader
        eyebrow="Frota"
        title="Categorias de Veículos"
        description="Configure as classes de veículos disponíveis (Economy, Executive, Van, TukTuk…). Cada veículo é atribuído a uma categoria; cada tour pode ter preços diferentes por categoria."
      >
        <GoldButton onClick={() => { setEditing(null); setShow(true); }} data-testid="add-category-btn">
          <Plus className="w-3.5 h-3.5" /> Nova categoria
        </GoldButton>
      </PageHeader>

      {items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-slate-300 text-sm text-slate-500">
          Nenhuma categoria ainda.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.key} className={`border ${c.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-60"} overflow-hidden`} data-testid={`cat-card-${c.key}`}>
              {c.image_url ? (
                <img src={c.image_url} alt={c.label} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon className="w-10 h-10" /></div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-lg text-[#0a0f1e]">{c.label}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{c.key}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggle(c)} title={c.active ? "Desativar" : "Activar"} data-testid={`toggle-cat-${c.key}`} className={`px-2 py-1 text-[9px] uppercase tracking-widest font-bold border ${c.active ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-500"}`}>{c.active ? "Activo" : "Inactivo"}</button>
                    <button onClick={() => { setEditing(c); setShow(true); }} className="p-1.5 text-slate-500 hover:text-[#f59e0b]" data-testid={`edit-cat-${c.key}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(c)} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`delete-cat-${c.key}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {c.description && <p className="text-xs text-slate-600 mt-2 leading-snug">{c.description}</p>}
                <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-500">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.capacity_pax} pax</span>
                  <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {c.capacity_bags} malas</span>
                  <span className="flex items-center gap-1 text-slate-400"><GripVertical className="w-3 h-3" /> #{c.sort_order}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {show && <CategoryModal cat={editing} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function CategoryModal({ cat, onClose, onSaved }) {
  const isEdit = !!cat;
  const [f, setF] = useState(cat || {
    key: "", label: "", description: "",
    capacity_pax: "1-4", capacity_bags: 2,
    image_url: "", icon: "car", sort_order: 100,
    base_price_eur: 10, price_per_km: 1.2, price_per_minute: 0.3, min_advance_hours: 2,
    zone_pricing: {}, season_multipliers: {},
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [zones, setZones] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [tab, setTab] = useState("main");

  React.useEffect(() => {
    api.get("/admin/zones?active=true").then((r) => setZones(r.data)).catch(() => {});
    api.get("/admin/seasons?active=true").then((r) => setSeasons(r.data)).catch(() => {});
  }, []);

  const setZonePricing = (zoneKey, field, value) => {
    const zp = { ...(f.zone_pricing || {}) };
    const entry = { ...(zp[zoneKey] || {}) };
    if (value === "" || value == null) delete entry[field];
    else entry[field] = Number(value);
    if (Object.keys(entry).length === 0) delete zp[zoneKey];
    else zp[zoneKey] = entry;
    setF({ ...f, zone_pricing: zp });
  };
  const setSeasonMultiplier = (seasonId, value) => {
    const sm = { ...(f.season_multipliers || {}) };
    if (value === "" || value == null) delete sm[seasonId];
    else sm[seasonId] = Number(value);
    setF({ ...f, season_multipliers: sm });
  };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Imagem excede 4MB"); return; }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setF((prev) => ({ ...prev, image_url: url }));
    } catch (err) { toast.error(`Erro ao enviar: ${formatApiError(err)}`); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (isEdit) {
        const { key: _k, ...patch } = f;
        await api.patch(`/admin/vehicle-categories/${cat.key}`, patch);
        toast.success("Categoria actualizada");
      } else {
        await api.post("/admin/vehicle-categories", f);
        toast.success("Categoria criada");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="cat-modal">
      <div className="bg-white border border-[#f59e0b]/30 w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl text-[#0a0f1e]">{isEdit ? "Editar categoria" : "Nova categoria de veículo"}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-[#0a0f1e]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Fld l="Chave (snake_case, permanente)"><Input value={f.key} onChange={(e) => setF({ ...f, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} disabled={isEdit} required data-testid="cat-key" placeholder="tuktuk" /></Fld>
            <Fld l="Nome visível"><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} required data-testid="cat-label" placeholder="TukTuk" /></Fld>
          </div>
          <Fld l="Descrição"><Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} data-testid="cat-description" /></Fld>
          <div className="grid grid-cols-3 gap-3">
            <Fld l="Capacidade pax"><Input value={f.capacity_pax} onChange={(e) => setF({ ...f, capacity_pax: e.target.value })} placeholder="1-3" data-testid="cat-pax" /></Fld>
            <Fld l="Malas"><Input type="number" min="0" value={f.capacity_bags} onChange={(e) => setF({ ...f, capacity_bags: Number(e.target.value) || 0 })} data-testid="cat-bags" /></Fld>
            <Fld l="Ordem"><Input type="number" min="0" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) || 0 })} data-testid="cat-sort" /></Fld>
          </div>

          <div className="border border-slate-200 bg-slate-50/60 p-4 space-y-3">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[#f59e0b] font-bold">Tarifa de transfer personalizado</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Aplicada apenas em transfers dinâmicos (cliente escolhe origem+destino). Tours e rotas fixas usam preços próprios.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Fld l="Preço base €"><Input type="number" min="0" step="0.5" value={f.base_price_eur} onChange={(e) => setF({ ...f, base_price_eur: Number(e.target.value) || 0 })} data-testid="cat-base" /></Fld>
              <Fld l="€ por km"><Input type="number" min="0" step="0.1" value={f.price_per_km} onChange={(e) => setF({ ...f, price_per_km: Number(e.target.value) || 0 })} data-testid="cat-per-km" /></Fld>
              <Fld l="€ por minuto"><Input type="number" min="0" step="0.05" value={f.price_per_minute} onChange={(e) => setF({ ...f, price_per_minute: Number(e.target.value) || 0 })} data-testid="cat-per-min" /></Fld>
              <Fld l="Antecedência mín. (horas)"><Input type="number" min="0" step="0.5" value={f.min_advance_hours} onChange={(e) => setF({ ...f, min_advance_hours: Number(e.target.value) || 0 })} data-testid="cat-advance" /></Fld>
            </div>
            <div className="text-[10px] text-slate-600 font-mono">
              Exemplo Lisboa→Sintra (~30 km / 45 min):
              <strong className="text-[#0a0f1e] ml-1">
                € {(Number(f.base_price_eur || 0) + 30 * Number(f.price_per_km || 0) + 45 * Number(f.price_per_minute || 0)).toFixed(2)}
              </strong>
            </div>
          </div>

          {/* ZONE PRICING OVERRIDES (Msg 658) */}
          {zones.length > 0 && (
            <div className="border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#f59e0b]" />
                <p className="text-[11px] uppercase tracking-widest text-[#f59e0b] font-bold">Tarifas por Zona (opcional)</p>
              </div>
              <p className="text-[10px] text-slate-500">Sobrescrever tarifa quando o transfer opera dentro duma zona específica. Vazio = usa tarifa base acima.</p>
              <div className="space-y-2">
                {zones.map((z) => {
                  const entry = f.zone_pricing?.[z.key] || {};
                  return (
                    <div key={z.key} className="border border-slate-100 p-3 grid grid-cols-4 gap-2 items-center" data-testid={`cat-zone-${z.key}`}>
                      <div className="text-xs font-semibold text-[#0a0f1e]">{z.label}</div>
                      <div>
                        <Input type="number" min="0" step="0.5" value={entry.base_price_eur ?? ""} placeholder={`€${Number(f.base_price_eur || 0).toFixed(0)}`}
                          onChange={(e) => setZonePricing(z.key, "base_price_eur", e.target.value)} className="h-8 text-sm" data-testid={`cat-zone-base-${z.key}`} />
                      </div>
                      <div>
                        <Input type="number" min="0" step="0.1" value={entry.price_per_km ?? ""} placeholder={`€${Number(f.price_per_km || 0).toFixed(2)}/km`}
                          onChange={(e) => setZonePricing(z.key, "price_per_km", e.target.value)} className="h-8 text-sm" data-testid={`cat-zone-km-${z.key}`} />
                      </div>
                      <div>
                        <Input type="number" min="0" step="0.05" value={entry.price_per_minute ?? ""} placeholder={`€${Number(f.price_per_minute || 0).toFixed(2)}/min`}
                          onChange={(e) => setZonePricing(z.key, "price_per_minute", e.target.value)} className="h-8 text-sm" data-testid={`cat-zone-min-${z.key}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* SEASON MULTIPLIERS (Msg 658) */}
          {seasons.length > 0 && (
            <div className="border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CalendarRange className="w-4 h-4 text-[#f59e0b]" />
                <p className="text-[11px] uppercase tracking-widest text-[#f59e0b] font-bold">Multiplicadores por Temporada (opcional)</p>
              </div>
              <p className="text-[10px] text-slate-500">Multiplica todos os componentes da tarifa (base, km, min) quando a temporada está activa. Ex: 1.25 = +25%. Vazio = sem multiplicador (fica 1.0).</p>
              <div className="grid grid-cols-2 gap-2">
                {seasons.map((s) => (
                  <div key={s.id} className="border border-slate-100 p-2.5 flex items-center gap-2" data-testid={`cat-season-${s.id}`}>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-[#0a0f1e]">{s.name}</div>
                      <div className="text-[10px] text-slate-500">{s.scope === "global" ? "Global" : `Zona: ${s.zone_key}`}</div>
                    </div>
                    <Input type="number" min="0" step="0.05" value={f.season_multipliers?.[s.id] ?? ""} placeholder="1.0"
                      onChange={(e) => setSeasonMultiplier(s.id, e.target.value)} className="w-20 h-8 text-sm text-right" data-testid={`cat-season-mult-${s.id}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Foto</label>
            {f.image_url ? (
              <div className="border border-slate-200 p-2 flex items-center gap-3">
                <img src={f.image_url} alt="preview" className="w-20 h-20 object-cover border border-slate-200" />
                <button type="button" onClick={() => setF({ ...f, image_url: "" })} className="text-xs uppercase tracking-widest text-red-600 hover:text-red-800 font-bold">Remover</button>
              </div>
            ) : (
              <label className="border border-dashed border-slate-300 hover:border-[#f59e0b] p-4 flex flex-col items-center gap-1 cursor-pointer" data-testid="cat-image-upload">
                <ImagePlus className="w-5 h-5 text-slate-400" />
                <span className="text-xs text-slate-500">{uploading ? "A enviar…" : "Carregar imagem (JPG/PNG, ≤4MB)"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={pickImage} />
              </label>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <GhostButton type="button" onClick={onClose}>Cancelar</GhostButton>
            <GoldButton type="submit" disabled={saving || uploading} data-testid="save-cat">{saving ? "A guardar…" : "Guardar"}</GoldButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function Fld({ l, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">{l}</label>
      {children}
    </div>
  );
}
